import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { Order } from '@/shared/types';

/**
 * Заказ, отправленный в аптеку, но ещё не подтверждённый: shopping-service
 * держит его в PRE_PROCESSING, а список заказов этот статус отсекает.
 */
export interface PendingOrder {
  order: Order;
  /** Когда отправили — по нему отсекаем зависшие */
  sentAt: number;
}

interface PendingOrdersState {
  items: PendingOrder[];
}

const STORAGE_KEY = 'pending_orders';

/**
 * Если аптека не ответила за это время, карточку убираем: заказ мог
 * зависнуть насовсем, и висеть в списке вечно он не должен
 */
const TTL_MS = 30 * 60 * 1000;

const isFresh = ({ sentAt }: PendingOrder) => Date.now() - sentAt < TTL_MS;

function read(): PendingOrder[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];

    const parsed = JSON.parse(raw) as PendingOrder[];
    return Array.isArray(parsed) ? parsed.filter(isFresh) : [];
  } catch {
    return [];
  }
}

function write(items: PendingOrder[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // Приватный режим и переполненное хранилище: карточка не настолько
    // важна, чтобы из-за неё ронять оформление
  }
}

const pendingOrdersSlice = createSlice({
  name: 'pendingOrders',
  initialState: { items: read() } as PendingOrdersState,
  reducers: {
    orderSubmitted(state, action: PayloadAction<Order>) {
      state.items = [
        { order: action.payload, sentAt: Date.now() },
        ...state.items.filter((item) => item.order._id !== action.payload._id),
      ];
      write(state.items);
    },

    /** Заказ доехал до списка или не состоялся — карточка больше не нужна */
    ordersSettled(state, action: PayloadAction<string[]>) {
      const settled = new Set(action.payload);

      state.items = state.items.filter(
        (item) => !settled.has(item.order._id) && isFresh(item)
      );
      write(state.items);
    },
  },
});

export const { orderSubmitted, ordersSettled } = pendingOrdersSlice.actions;
export const pendingOrdersReducer = pendingOrdersSlice.reducer;
